import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { useAnalytics } from '../../context/AnalyticsContext';
import { Settings, CheckCircle, AlertCircle, ExternalLink, RefreshCw, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { useLocale } from '../../context/LocaleContext';

interface ProviderCardProps {
  provider: 'zoho' | 'powerbi';
  onSetup?: () => void;
  onManage?: () => void;
}

const ProviderCard: React.FC<ProviderCardProps> = ({ provider, onSetup, onManage }) => {
  const { state, actions } = useAnalytics();
  const [showCredentials, setShowCredentials] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { formatDate } = useLocale();
  
  const providerState = state.providers[provider];
  const config = state.configuration?.[provider];
  const schema = state.schema[provider];

  const providerInfo = {
    zoho: {
      name: 'Zoho Analytics',
      description: 'Connect your Zoho Analytics workspace for advanced reporting',
      icon: '🔍',
      color: 'blue',
      setupUrl: 'https://api-console.zoho.com/',
    },
    powerbi: {
      name: 'Power BI',
      description: 'Connect your Microsoft Power BI workspace for data insights',
      icon: '📊',
      color: 'yellow',
      setupUrl: 'https://dev.powerbi.com/apps',
    },
  };

  const info = providerInfo[provider];

  useEffect(() => {
    // Check provider status when component mounts
    if (config?.enabled) {
      actions.checkProviderStatus(provider);
    }
  }, [config?.enabled, provider]);

  const handleRefreshCredentials = async () => {
    setIsRefreshing(true);
    try {
      await actions.refreshCredentials(provider);
    } catch (error) {
      console.error('Failed to refresh credentials:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRevokeCredentials = async () => {
    if (window.confirm('Are you sure you want to revoke access? This will disconnect the provider.')) {
      try {
        await actions.revokeCredentials(provider);
      } catch (error) {
        console.error('Failed to revoke credentials:', error);
      }
    }
  };

  const getStatusIndicator = () => {
    if (providerState.loading) {
      return (
        <div className="flex items-center text-gray-600">
          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
          <span className="text-sm font-medium">Loading...</span>
        </div>
      );
    }

    if (providerState.error) {
      return (
        <div className="flex items-center text-red-600">
          <AlertCircle className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">Error</span>
        </div>
      );
    }

    if (providerState.authorized && providerState.connected) {
      return (
        <div className="flex items-center text-green-600">
          <CheckCircle className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">Connected</span>
        </div>
      );
    }

    if (providerState.configured) {
      return (
        <div className="flex items-center text-yellow-600">
          <AlertCircle className="w-4 h-4 mr-1" />
          <span className="text-sm font-medium">Configured</span>
        </div>
      );
    }

    return (
      <div className="flex items-center text-gray-600">
        <AlertCircle className="w-4 h-4 mr-1" />
        <span className="text-sm font-medium">Not Configured</span>
      </div>
    );
  };

  const renderConnectionDetails = () => {
    if (!providerState.credentialStatus) return null;

    const status = providerState.credentialStatus;

    return (
      <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-text-primary">Connection Details</span>
          <button
            onClick={() => setShowCredentials(!showCredentials)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
          >
            {showCredentials ? (
              <>
                <EyeOff className="w-4 h-4 mr-1" />
                Hide
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-1" />
                Show
              </>
            )}
          </button>
        </div>
        
        {showCredentials && (
          <div className="space-y-1 text-sm text-text-secondary">
            {status.expiresAt && (
              <div>
                <strong>Expires:</strong> {formatDate(status.expiresAt, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {status.isExpiring && (
                  <span className="ml-2 text-yellow-600 font-medium">(Expiring Soon)</span>
                )}
              </div>
            )}
            {status.settings?.workspace && (
              <div><strong>Workspace:</strong> {status.settings.workspace}</div>
            )}
            {status.settings?.defaultTable && (
              <div><strong>Default Table:</strong> {status.settings.defaultTable}</div>
            )}
            {status.settings?.orgIdOrEmail && (
              <div><strong>Organization:</strong> {status.settings.orgIdOrEmail}</div>
            )}
            {status.settings?.aadTenantId && (
              <div><strong>AAD Tenant:</strong> {status.settings.aadTenantId}</div>
            )}
            {status.settings?.groupId && (
              <div><strong>Group ID:</strong> {status.settings.groupId}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSchemaInfo = () => {
    if (!schema?.data) return null;

    return (
      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-green-800">Schema Available</span>
          <span className="text-xs text-green-600">
            {schema.data.tables.length} tables discovered
          </span>
        </div>
        <div className="text-xs text-green-700">
          Last updated: {schema.lastUpdated ? formatDate(schema.lastUpdated, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Unknown'}
        </div>
        {schema.data.tables.slice(0, 3).map((table, index) => (
          <div key={index} className="text-xs text-green-600 mt-1">
            • {table.displayName || table.name} ({table.columns.length} columns)
          </div>
        ))}
        {schema.data.tables.length > 3 && (
          <div className="text-xs text-green-600 mt-1">
            ... and {schema.data.tables.length - 3} more tables
          </div>
        )}
      </div>
    );
  };

  const renderActions = () => {
    const actions_list = [];

    if (!providerState.configured) {
      actions_list.push(
        <Button key="setup" onClick={onSetup} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Setup
        </Button>
      );
      
      actions_list.push(
        <Button 
          key="docs" 
          onClick={() => window.open(info.setupUrl, '_blank')} 
          variant="outline" 
          size="sm"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Setup Guide
        </Button>
      );
    } else {
      if (providerState.authorized) {
        actions_list.push(
          <Button 
            key="refresh" 
            onClick={handleRefreshCredentials} 
            variant="outline" 
            size="sm"
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        );

        actions_list.push(
          <Button 
            key="revoke" 
            onClick={handleRevokeCredentials} 
            variant="outline" 
            size="sm"
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Revoke
          </Button>
        );
      }

      actions_list.push(
        <Button key="manage" onClick={onManage} variant="outline" size="sm">
          <Settings className="w-4 h-4 mr-2" />
          Manage
        </Button>
      );
    }

    return actions_list;
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 bg-${info.color}-100 rounded-lg flex items-center justify-center`}>
            <span className="text-lg">{info.icon}</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{info.name}</h3>
            <p className="text-sm text-text-secondary">{info.description}</p>
          </div>
        </div>
        {getStatusIndicator()}
      </div>

      {providerState.error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <span className="text-sm text-red-800">{providerState.error}</span>
          </div>
        </div>
      )}

      {renderConnectionDetails()}
      {renderSchemaInfo()}

      <div className="flex flex-wrap gap-2 mt-4">
        {renderActions()}
      </div>
    </Card>
  );
};

export default ProviderCard;